/**
 * Aggregate transaction data and return raw chart arrays
 * for Guest Count (column) and Avg Spend per Guest (spline).
 */
export function getAvgSpendGuestChartData({
  objects,
  selectedStoreCode,
  selectedMonthName,
  selectedYear,
}) {
  // 1) Filter completed transactions and apply year/month/store filters
  let filtered = (objects || []).filter((tx) => tx.status === "C");
  filtered = filtered.filter((sale) => {
    const d = new Date(sale.tran_date);
    const year = d.getFullYear();
    const month = d.toLocaleString("default", { month: "long" });
    const store = sale.store_code.replace("ANGELSPIZZA_", "");

    const okYear = selectedYear ? year === parseInt(selectedYear, 10) : true;
    const okMonth = selectedMonthName ? month === selectedMonthName : true;
    const okStore = selectedStoreCode ? store === selectedStoreCode : true;

    return okYear && okMonth && okStore;
  });

  // 2) Aggregate sales and customer counts by month
  const salesByMonth = {};
  const countsByMonth = {};

  filtered.forEach((sale) => {
    if (sale.cust_name && sale.cust_name !== "-") {
      const d = new Date(sale.tran_date);
      const month = d.toLocaleString("default", { month: "long" });
      const year = d.getFullYear();
      const key = `${month} ${year}`;

      salesByMonth[key] = salesByMonth[key] || [];
      countsByMonth[key] = countsByMonth[key] || {};

      salesByMonth[key].push(sale);
      countsByMonth[key][sale.cust_name] =
        (countsByMonth[key][sale.cust_name] || 0) + 1;
    }
  });

  // 3) Compute totals, counts, and averages per month
  const labels = Object.keys(salesByMonth);
  const monthlyTotals = labels.map((key) => {
    return salesByMonth[key].reduce((sum, sale) => {
      const total = parseFloat(sale.total) || 0;
      const discount = parseFloat(sale.discount_amount) || 0;
      const tax = parseFloat(sale.total_tax) || 0;
      const gross = total + discount + tax;
      return sum + gross;
    }, 0);
  });
  const monthlyCounts = labels.map((key) => {
    return Object.values(countsByMonth[key]).reduce((sum, c) => sum + c, 0);
  });
  const monthlyAverages = monthlyTotals.map((tot, i) =>
    monthlyCounts[i] ? tot / monthlyCounts[i] : 0,
  );

  return { labels, monthlyCounts, monthlyAverages };
}

/**
 * Build Highcharts options for dual-axis Guest Count (area) +
 * Avg Spend per Guest (spline with markers) chart.
 */
export function getAvgSpendGuestChartOptions({
  labels,
  monthlyCounts,
  monthlyAverages,
}) {
  return {
    chart: { zoomType: "xy" },
    title: { text: null },
    xAxis: {
      categories: labels,
      crosshair: true,
    },
    yAxis: [
      {
        title: { text: "Guest Count" },
        labels: {
          formatter() {
            return this.value.toLocaleString();
          },
        },
      },
      {
        title: { text: "Avg Spend per Guest (PHP)" },
        labels: {
          formatter() {
            return (
              "\u20B1" +
              this.value.toLocaleString(undefined, {
                maximumFractionDigits: 2,
              })
            );
          },
        },
        opposite: true,
      },
    ],
    tooltip: { shared: true },
    series: [
      {
        name: "Guest Count",
        type: "column",
        data: monthlyCounts,
        color: "#006096",
        yAxis: 0,
        tooltip: {
          valueSuffix: " guests",
        },
      },
      {
        name: "Avg Spend per Guest",
        type: "column",
        data: monthlyAverages.map((v) => parseFloat(v.toFixed(2))),
        color: "#ffd043",
        yAxis: 1,
        tooltip: {
          valuePrefix: "\u20B1",
          valueDecimals: 2,
        },
      },
    ],
    credits: { enabled: false },
  };
}
